export const CLIENT_COMPATIBILITY_MESSAGE_DURATION_SECONDS = 20;
export const CLIENT_COMPATIBILITY_NAV_MESSAGE_DURATION_SECONDS = 6;
export const CLIENT_COMPATIBILITY_REMINDER_INTERVAL_MS = 60 * 60 * 1000;
export const CLIENT_COMPATIBILITY_MESSAGE_KEY = 'client-compatibility-warning';
export const CLIENT_COMPATIBILITY_NAV_MESSAGE_KEY = 'client-compatibility-nav-warning';

const MINIMUM_REQUIREMENTS = {
  ios: {
    label: 'iOS',
    minimumVersion: '15.6',
  },
  android: {
    label: 'Android',
    minimumVersion: '10',
  },
  harmonyos: {
    label: 'HarmonyOS',
    minimumVersion: '4.1',
  },
};

const GENERIC_COMPATIBILITY_MESSAGE =
  '检测到当前系统版本较低。为保证正常使用，请升级到 iOS 15.6+、Android 10+ 或 HarmonyOS 4.1+ 以上的版本。';

const normalizeVersion = (value) => {
  const raw = String(value || '').replace(/_/g, '.').trim();
  const match = raw.match(/\d+(?:\.\d+)*/);
  return match ? match[0] : '';
};

export const compareVersions = (left, right) => {
  const leftParts = normalizeVersion(left).split('.').filter(Boolean).map(Number);
  const rightParts = normalizeVersion(right).split('.').filter(Boolean).map(Number);
  const length = Math.max(leftParts.length, rightParts.length);

  for (let index = 0; index < length; index += 1) {
    const leftValue = Number.isFinite(leftParts[index]) ? leftParts[index] : 0;
    const rightValue = Number.isFinite(rightParts[index]) ? rightParts[index] : 0;
    if (leftValue > rightValue) return 1;
    if (leftValue < rightValue) return -1;
  }

  return 0;
};

const getBrowserEnv = () => {
  if (typeof navigator === 'undefined') {
    return {};
  }

  return {
    userAgent: navigator.userAgent,
    platform: navigator.platform,
    maxTouchPoints: navigator.maxTouchPoints,
  };
};

export const detectClientPlatform = (env = getBrowserEnv()) => {
  const userAgent = String(env.userAgent || '');
  const platform = String(env.platform || '');
  const maxTouchPoints = Number(env.maxTouchPoints || 0);

  const harmonyMatch = userAgent.match(/(?:HarmonyOS|OpenHarmony)[/ ]([0-9._]+)/i);
  if (harmonyMatch) {
    return {
      platformKey: 'harmonyos',
      currentVersion: normalizeVersion(harmonyMatch[1]),
    };
  }

  const iosMatch = userAgent.match(/(?:iPhone|iPad|iPod).*OS ([0-9_]+)/i);
  if (iosMatch) {
    return {
      platformKey: 'ios',
      currentVersion: normalizeVersion(iosMatch[1]),
    };
  }

  const isDesktopModeIpad = platform === 'MacIntel' && maxTouchPoints > 1 && /Mobile\/\S+ Safari\//i.test(userAgent);
  const safariVersionMatch = userAgent.match(/Version\/([0-9.]+)/i);
  if (isDesktopModeIpad && safariVersionMatch) {
    return {
      platformKey: 'ios',
      currentVersion: normalizeVersion(safariVersionMatch[1]),
    };
  }

  const androidMatch = userAgent.match(/Android ([0-9.]+)/i);
  if (androidMatch) {
    return {
      platformKey: 'android',
      currentVersion: normalizeVersion(androidMatch[1]),
    };
  }

  return null;
};

export const getClientCompatibility = (env) => {
  const detected = detectClientPlatform(env);
  if (!detected) {
    return {
      supported: true,
      recognized: false,
    };
  }

  const requirement = MINIMUM_REQUIREMENTS[detected.platformKey];
  if (!requirement || !detected.currentVersion) {
    return {
      supported: true,
      recognized: false,
    };
  }

  const supported = compareVersions(detected.currentVersion, requirement.minimumVersion) >= 0;
  return {
    supported,
    recognized: true,
    platformKey: detected.platformKey,
    platformLabel: requirement.label,
    currentVersion: detected.currentVersion,
    minimumVersion: requirement.minimumVersion,
  };
};

export const buildClientCompatibilityMessage = (compatibility) => {
  if (!compatibility || compatibility.supported) {
    return '';
  }

  if (compatibility.platformLabel && compatibility.currentVersion && compatibility.minimumVersion) {
    return `检测到当前系统版本 ${compatibility.platformLabel} ${compatibility.currentVersion}，为保证正常使用，请升级到 ${compatibility.platformLabel} ${compatibility.minimumVersion} 以上。`;
  }

  return GENERIC_COMPATIBILITY_MESSAGE;
};

const getLocalStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage || null;
};

export const getClientCompatibilityReminderKey = (compatibility, appVersion = '') => {
  const versionPart = String(appVersion || 'dev').trim() || 'dev';
  const platformPart = String(compatibility?.platformKey || 'unknown').trim() || 'unknown';
  const currentPart = String(compatibility?.currentVersion || 'unknown').trim() || 'unknown';
  const minimumPart = String(compatibility?.minimumVersion || 'unknown').trim() || 'unknown';
  return `clientCompatibilityReminder:${versionPart}:${platformPart}:${currentPart}:${minimumPart}`;
};

export const shouldShowClientCompatibilityReminder = (compatibility, options = {}) => {
  if (!compatibility || compatibility.supported) {
    return false;
  }

  const nowMs = Number.isFinite(options.nowMs) ? options.nowMs : Date.now();
  const intervalMs = Number.isFinite(options.intervalMs)
    ? options.intervalMs
    : CLIENT_COMPATIBILITY_REMINDER_INTERVAL_MS;
  const storage = options.storage === undefined ? getLocalStorage() : options.storage;

  if (!storage) {
    return true;
  }

  try {
    const key = getClientCompatibilityReminderKey(compatibility, options.appVersion);
    const lastShownRaw = storage.getItem(key);
    const lastShownMs = Number(lastShownRaw);
    if (lastShownRaw != null && Number.isFinite(lastShownMs) && nowMs - lastShownMs < intervalMs) {
      return false;
    }
    storage.setItem(key, String(nowMs));
    return true;
  } catch (_) {
    return true;
  }
};
